import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { createResearchReport, getResearchProgress } from "@/api/research";

const QUIZ = [
  { key: "purpose", label: "研报用途", options: ["投资决策", "行业研究", "财务分析", "估值参考"] },
  { key: "direction", label: "投资方向", options: ["买入", "卖出", "持有", "观望"] },
  { key: "horizon", label: "持仓期限", options: ["短期（1-3个月）", "中期（3-12个月）", "长期（1年以上）"] },
  { key: "focus", label: "重点维度", options: ["基本面", "技术面", "资金面", "消息面", "综合"] },
  { key: "risk", label: "风险偏好", options: ["保守", "稳健", "进取"] },
  { key: "valuation", label: "估值模型", options: ["DCF 估值", "相对估值(PE/PB)", "两者都要", "不需要"] },
];

const STAGES = [
  { key: "data", label: "数据采集", desc: "拉取实时行情与近5年财务数据" },
  { key: "finance", label: "财务解析", desc: "分析三表趋势与关键指标" },
  { key: "valuation", label: "估值建模", desc: "DCF 与相对估值计算" },
  { key: "writing", label: "撰写成稿", desc: "生成投资论点与风险提示" },
];

export default function ResearchGenerate() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const step = searchParams.get("step");
  const stockName = searchParams.get("name") || "";

  const [quizStep, setQuizStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [more, setMore] = useState("");
  const [generating, setGenerating] = useState(false);
  const [reportId, setReportId] = useState<number | null>(null);
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState("");
  const [status, setStatus] = useState("");
  const pollRef = useRef<any>(null);

  const showQuiz = step === "quiz" && !generating;

  const handleAnswer = (key: string, val: string) => {
    setAnswers(prev => ({ ...prev, [key]: val }));
    if (quizStep < QUIZ.length - 1) {
      setTimeout(() => setQuizStep(s => s + 1), 150);
    } else {
      setGenerating(true);
    }
  };

  useEffect(() => {
    if (!generating || reportId) return;
    (async () => {
      try {
        const extra = more.trim() ? `\n额外要求：${more}` : "";
        const pref = Object.entries(answers).map(([k, v]) => `${k}: ${v}`).join("，");
        const r = await createResearchReport(code || "", undefined, undefined, undefined, pref + extra);
        const id = r?.id;
        if (!id) { setReportId(-1); return; }
        setReportId(id);
        navigate(`/research/library`, { replace: true });
      } catch(e) { alert(e instanceof Error ? e.message : "生成失败"); setReportId(-1); }
    })();
  }, [generating]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const rid = params.get("rid");
    if (!rid) return;
    setReportId(Number(rid));
    setGenerating(true);

    pollRef.current = setInterval(async () => {
      try {
        const p = await getResearchProgress(Number(rid));
        setProgress(p.progress || 0);
        setStage(p.stage || "");
        setStatus(p.status || "");

        if (p.status === "completed" || p.progress >= 100) {
          clearInterval(pollRef.current);
          setTimeout(() => navigate(`/research/report/${rid}`, { replace: true }), 1500);
        }
        if (p.status === "failed") {
          clearInterval(pollRef.current);
        }
      } catch { /* ignore */ }
    }, 2000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);



  if (showQuiz) {
    const q = QUIZ[quizStep];
    if (!q) return null;
    const currentAnswer = answers[q.key];

    return (
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-8">
          {quizStep > 0 && <button onClick={() => { if (quizStep > 0) setQuizStep(s => s - 1); }} className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center cursor-pointer"><i className="ri-arrow-left-s-line text-foreground-600 text-lg" /></button>}
          <div className="flex-1">
            <div className="flex gap-1 mb-1">{QUIZ.map((_, i) => (<div key={i} className="flex-1 h-1 rounded-full" style={{ background: i <= quizStep ? "#6366f1" : "#E2E8F0" }} />))}</div>
            <div className="flex justify-between text-[10px] text-foreground-400"><span>{quizStep + 1}/{QUIZ.length}</span><span>约1分钟</span></div>
          </div>
        </div>
        {code && (
          <div className="flex items-center gap-2 mb-6 -mt-2">
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-indigo-50 border border-indigo-200/60">
              <i className="ri-stock-line text-indigo-500 text-xs" />
              <span className="text-sm font-semibold text-foreground-950">{stockName || code}</span>
              <span className="text-[10px] text-foreground-400 font-mono">{stockName ? code : ""}</span>
            </div>
          </div>
        )}
        <div className="mb-8">
          <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center mb-4"><i className="ri-ai-line text-indigo-500 text-xl" /></div>
          <h2 className="text-xl font-bold text-foreground-950 mb-2">AI 研报助手</h2>
          <p className="text-sm text-foreground-500">为了生成更精准的研报，请告诉我您的需求：</p>
        </div>
        <div className="mb-6">
          <div className="bg-white rounded-xl p-4 border border-background-200/60 mb-4">
            <div className="text-sm font-semibold text-foreground-950 mb-3">{q.label}</div>
            <div className="space-y-2">{q.options.map(opt => (
              <button key={opt} onClick={() => handleAnswer(q.key, opt)}
                className={`w-full text-left px-4 py-3 rounded-xl text-sm transition-all cursor-pointer ${currentAnswer === opt ? "bg-indigo-50 border border-indigo-300 text-indigo-700 font-medium" : "bg-background-50 border border-background-200/60 text-foreground-700 hover:border-indigo-200"}`}>{opt}</button>
            ))}</div>
          </div>
          {quizStep === QUIZ.length - 1 && (
            <div className="bg-white rounded-xl p-4 border border-background-200/60">
              <label className="text-xs font-medium text-foreground-600 mb-1.5 block">其他需求（选填）</label>
              <textarea value={more} onChange={e => setMore(e.target.value)} placeholder="关注 ESG、想了解竞争对手、需要可比公司分析..." rows={2}
                className="w-full bg-background-50 rounded-xl px-3 py-2 text-sm text-foreground-950 placeholder:text-foreground-300 outline-none border-none resize-none" />
            </div>
          )}
        </div>
      </div>
    );
  }

  const activeStage = Math.min(3, Math.floor(progress / 25));

  return (
    <div className="flex-1 overflow-y-auto px-4 py-4">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center cursor-pointer"><i className="ri-arrow-left-s-line text-foreground-600 text-lg" /></button>
        <h1 className="text-lg font-bold text-foreground-950">研报生成中</h1>
      </div>
      <div className="text-center mb-6">
        <div className="text-base font-bold text-foreground-950">{stockName || code || "股票"} {code && stockName && <span className="text-xs text-foreground-400 font-normal">{code}</span>} {stage && <span className="text-xs text-foreground-400 font-normal ml-2">{stage}</span>}</div>
        <div className="text-xs text-foreground-400 mt-1">{progress < 100 ? `预计剩余 ${Math.ceil((100 - progress) / 20)} 分钟` : "已完成！即将跳转..."}</div>
      </div>
      <div className="flex justify-center mb-8">
        <div className="relative w-24 h-24">
          <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="42" fill="none" stroke="#E2E8F0" strokeWidth="8" />
            <circle cx="50" cy="50" r="42" fill="none" stroke="#6366f1" strokeWidth="8"
              strokeDasharray={`${2 * Math.PI * 42}`} strokeDashoffset={`${2 * Math.PI * 42 * (1 - progress / 100)}`} strokeLinecap="round" />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center"><span className="text-xl font-bold text-foreground-950">{Math.round(progress)}%</span></div>
        </div>
      </div>
      <div className="space-y-4">
        {STAGES.map((s, i) => {
          const st = i < activeStage ? "done" : i === activeStage ? "active" : "pending";
          return (
            <div key={s.key} className="flex items-start gap-3">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${st === "done" ? "bg-green-500" : st === "active" ? "bg-indigo-500" : "bg-background-200"}`}>
                {st === "done" && <i className="ri-check-line text-white text-xs" />}
                {st === "active" && <i className="ri-loader-4-line animate-spin text-white text-xs" />}
                {st === "pending" && <span className="text-foreground-400 text-xs">{i + 1}</span>}
              </div>
              <div className="flex-1">
                <div className={`text-sm font-semibold ${st === "done" ? "text-green-600" : st === "active" ? "text-foreground-950" : "text-foreground-400"}`}>{s.label}</div>
                <div className={`text-[10px] ${st === "active" ? "text-foreground-500" : "text-foreground-300"}`}>{st === "done" ? "已完成" : st === "active" ? s.desc : "等待中"}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
